type DragStart = Pick<PointerEvent, "pointerId" | "clientX" | "clientY">;

/** One captured desktop gesture. Paint only the latest move each frame, but
 * always apply the release position before committing the gesture. */
export function bindPointerDrag({
  event,
  target,
  threshold = 3,
  onStart,
  onMove,
  onFinish,
}: {
  event: DragStart;
  target: Element;
  threshold?: number;
  onStart(): void;
  onMove(event: PointerEvent): void;
  onFinish(event: PointerEvent | null, state: { started: boolean; cancelled: boolean }): void;
}): () => void {
  const { pointerId, clientX, clientY } = event;
  let started = false;
  let finished = false;
  let frame: number | null = null;
  let latest: PointerEvent | null = null;

  const begin = (next: PointerEvent) => {
    if (!started && Math.hypot(next.clientX - clientX, next.clientY - clientY) >= threshold) {
      started = true;
      onStart();
    }
    return started;
  };
  const move = (next: PointerEvent) => {
    if (next.pointerId !== pointerId || !begin(next)) return;
    latest = next;
    if (frame != null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      const pending = latest;
      latest = null;
      if (pending && !finished) onMove(pending);
    });
  };
  const finish = (next: PointerEvent | null, cancelled: boolean) => {
    if (finished || (next && next.pointerId !== pointerId)) return;
    finished = true;
    if (frame != null) cancelAnimationFrame(frame);
    latest = null;
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", cancel);
    window.removeEventListener("blur", blur);
    target.removeEventListener("lostpointercapture", lostCapture);
    try {
      target.releasePointerCapture(pointerId);
    } catch {
      // Window listeners also support browsers without pointer capture.
    }
    if (!cancelled && next && begin(next)) onMove(next);
    onFinish(next, { started, cancelled });
  };
  const up = (next: PointerEvent) => finish(next, false);
  const cancel = (next: PointerEvent) => finish(next, true);
  const blur = () => finish(null, true);
  const lostCapture = (next: Event) => finish(next as PointerEvent, true);

  try {
    target.setPointerCapture(pointerId);
  } catch {
    // Capture can be unavailable for a synthetic event or an interrupted input.
  }
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", cancel);
  window.addEventListener("blur", blur);
  target.addEventListener("lostpointercapture", lostCapture);
  return () => finish(null, true);
}
